import { useEffect, useMemo } from 'react';
import { VideoPayload } from './types';
import { VideoPlayer } from './components/VideoPlayer';
import { Box, Typography } from '@mui/material';
import { WidgetProps } from '@features/dashboard/types';
import Widget, { Content, Header } from '../Widget';
import Settings from '../settings/Settings';
import { VideoList } from './components/VideoList';
import { useVideos } from './hooks/useVideos';
import { useVideosLayout } from './hooks/useVideosLayout';
import { useApi } from '@shared/hooks/useApi';
import { useScreenSize } from '@shared/hooks/useScreenSize';
import { NUMBER_OF_VIDEOS } from './constants';
import { useWidgetLifecycleTelemetry } from '../useWidgetLifecycleTelemetry';
type Props = WidgetProps & {
  payload?: VideoPayload;
};

export const Videos = ({ payload, ...otherProps }: Props) => {
  const { apiClient } = useApi();
  const { id, title } = otherProps;

  const videoLoader = useMemo(() => apiClient?.videosLoader({ limit: NUMBER_OF_VIDEOS }), [apiClient]);
  const { isMobile } = useScreenSize();

  const { containerRef, containerWidth, getGridSize } = useVideosLayout();
  useWidgetLifecycleTelemetry(id, title, 'Videos', payload);
  const {
    videos,
    selectedVideoId,
    isListVisible,
    fetchVideos,
    toggleVideoList,
    loadMoreVideos,
    hasMoreVideos,
    onNextVideo,
    onPreviousVideo,
    videoIndex,
    onVideoTypeChange,
    videoType,
    handleVideoClick,
    selectedVideoRef,
    onSearch,
    newVideoIds,
  } = useVideos(videoLoader);

  useEffect(() => {
    fetchVideos();
  }, [videoLoader]);

  const renderContent = () => {
    if (!selectedVideoId) {
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography color="text.secondary">Select a video to play</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ width: '100%', height: '100%', p: 2 }}>
        <VideoPlayer
          videoId={selectedVideoId}
          onNextVideo={onNextVideo}
          onPreviousVideo={onPreviousVideo}
          videoIndex={videoIndex}
          numberOfVideos={videos.length}
        />
      </Box>
    );
  };

  return (
    <Widget {...otherProps}>
      <Header title={otherProps.title}>
        <Settings id={`videos-widget-${otherProps.id}`} widgetId={otherProps.id} />
      </Header>
      <Content>
        <Box sx={{ display: 'flex', height: '100%' }}>
          <VideoList
            videos={videos}
            isListVisible={isListVisible}
            toggleVideoList={toggleVideoList}
            loadMoreVideos={loadMoreVideos}
            selectedVideoId={selectedVideoId}
            hasMoreVideos={hasMoreVideos}
            onVideoTypeChange={onVideoTypeChange}
            videoType={videoType}
            onSearch={onSearch}
            containerRef={containerRef}
            containerWidth={containerWidth}
            getGridSize={getGridSize}
            handleVideoClick={handleVideoClick}
            selectedVideoRef={selectedVideoRef}
            isMobile={isMobile}
            newVideoIds={newVideoIds}
          />
          {(!isMobile || !isListVisible) && renderContent()}
        </Box>
      </Content>
    </Widget>
  );
};
